import type { DeeplyReadonly, Query } from "@cubejs-client/core";
import { useCubeQuery } from "@cubejs-client/react";

import { useCubeJsApi } from "./CubeJsApiContext";
import type {
  LHQueryRecordType,
  LHUseCubeQueryResult,
} from "../../types/cubeTyping";

export const useCubeApiQuery = <
  TData,
  TQuery extends DeeplyReadonly<Query | Query[]> = DeeplyReadonly<
    Query | Query[]
  >,
>(
  query: TQuery,
) => {
  const { cubejsApi } = useCubeJsApi();
  const res = useCubeQuery(query, {
    cubeApi: cubejsApi,
  });

  if (res.resultSet) {
    // Change rawData to return all string values since pre-aggs always returns strings but could be turned off.
    // This simplifies the code since we always know values are strings

    // Store the original rawData method
    const originalRawData = res.resultSet.rawData.bind(res.resultSet);

    // Override the rawData method
    res.resultSet.rawData = () => {
      const rawData = originalRawData();
      return rawData.map((obj) => {
        const newObj: typeof obj = JSON.parse(JSON.stringify(obj));
        Object.keys(obj).forEach((key) => {
          newObj[key] = obj[key]?.toString() ?? "";
        });
        return newObj;
      });
    };
  }

  const resWithError = res as LHUseCubeQueryResult<
    TQuery,
    unknown extends TData ? LHQueryRecordType<TQuery> : TData
  >;

  resWithError.noPermissions =
    (res.error as unknown as { status: number | undefined })?.status === 403;

  return resWithError;
};
